import { ThemeProvider as EmotionThemeProvider } from "@emotion/react";
import { StyledEngineProvider, ThemeProvider } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { connectRouter, routerMiddleware } from "connected-react-router";
import { History } from "history";
import { PropsWithChildren } from "react";
import { Provider } from "react-redux";
import { ReducersMapObject, combineReducers } from "redux";
import { Middleware } from "redux";

import { theme } from "@tsp/tsp-frontend-components";
import { createEpicMiddleware } from "@tsp/utils/epics";

import { createStore } from "@tsp-core/store";

import { accountsApi } from "@tsp-domains/accounts/api";
import { accrualModelsApi } from "@tsp-domains/accrual-models/api";
import { companiesApi } from "@tsp-domains/companies/api";
import { contractTypesApi } from "@tsp-domains/contract-types/api";
import { contractorsAccountsApi } from "@tsp-domains/contractor-accounts/api";
import { contractsApi } from "@tsp-domains/contracts/api";
import { countriesApi } from "@tsp-domains/countries/api";
import { dashboardApi } from "@tsp-domains/dashboard/api";
import { departmentsApi } from "@tsp-domains/departments/api";
import { generalLedgerAccountsApi } from "@tsp-domains/general-ledger-accounts/api";
import { invoicesApi } from "@tsp-domains/invoices/api";
import { notificationsApi } from "@tsp-domains/notifications/api";
import { overviewApi } from "@tsp-domains/overview/api";
import { partiesApi } from "@tsp-domains/parties/api";
import { paymentSystemApi } from "@tsp-domains/payment-system/api";
import { positionsApi } from "@tsp-domains/positions/api";
import { projectsPortfolioApi } from "@tsp-domains/projects-portfolio/api";
import { soulEntitiesApi } from "@tsp-domains/soul-entities/api";
import { systemTypesApi } from "@tsp-domains/system-types/api";
import { workItemsApi } from "@tsp-domains/work-items/api";
import { ownWorkTypeAndSubTypeApi, workSubTypesApi, workTypesApi } from "@tsp-domains/work-types/api";

import { RouterSegment } from "@tsp-shared/connect-router";

import { EpicConfig } from "../utils/epics";

export interface StoreConfig<S extends RouterSegment> {
  middlewares?: Array<Middleware>;
  reducers: ReducersMapObject<Omit<S, keyof RouterSegment>>;
}

export interface AppFactoryConfig<S extends RouterSegment, D> {
  history: History;
  storeConfig: StoreConfig<S>;
  epicConfig: EpicConfig<S, D>;
}

export const createApplication = <S extends RouterSegment, D>(config: AppFactoryConfig<S, D>) => {
  const { epicConfig, storeConfig, history } = config;
  const { reducers, middlewares } = storeConfig;

  const epicMiddleware = createEpicMiddleware(epicConfig);

  const reducersMap = {
    router: connectRouter(history),
    ...reducers,
  } as unknown as ReducersMapObject<S>;

  const reducer = combineReducers<S>(reducersMap);

  const store = createStore<S>(reducer as any, [
    routerMiddleware(history),
    epicMiddleware.middleware,
    accountsApi.middleware,
    accrualModelsApi.middleware,
    companiesApi.middleware,
    contractTypesApi.middleware,
    contractorsAccountsApi.middleware,
    contractsApi.middleware,
    countriesApi.middleware,
    dashboardApi.middleware,
    departmentsApi.middleware,
    generalLedgerAccountsApi.middleware,
    invoicesApi.middleware,
    notificationsApi.middleware,
    overviewApi.middleware,
    ownWorkTypeAndSubTypeApi.middleware,
    partiesApi.middleware,
    paymentSystemApi.middleware,
    positionsApi.middleware,
    projectsPortfolioApi.middleware,
    soulEntitiesApi.middleware,
    systemTypesApi.middleware,
    workItemsApi.middleware,
    workSubTypesApi.middleware,
    workTypesApi.middleware,
    ...middlewares,
  ]);

  epicMiddleware.run();

  return function Application({ children }: PropsWithChildren<{}>) {
    return (
      <Provider store={store}>
        <StyledEngineProvider injectFirst>
          <EmotionThemeProvider theme={theme}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              {children}
            </ThemeProvider>
          </EmotionThemeProvider>
        </StyledEngineProvider>
      </Provider>
    );
  };
};
